import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Maximize, Minimize, Sparkles, Play, Pause, Scissors, Trash2, Undo2, Redo2, Magnet, ZoomIn, ZoomOut, Download, Keyboard, X, Copy, SkipBack, PanelLeftClose, PanelLeftOpen, PanelRightOpen } from "lucide-react";
import { refreshUsage } from "../UsageBar";
import logo from "../../assets/trimo-logo.png";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { Y, ASPECTS, FILTERS, uid, clamp, layout, totalOf, activeIdx, fmtMs, wrapText, emojify, probeDur, peaksOf } from "./editor/constants";
import { Stage } from "./editor/Stage";
import { Timeline } from "./editor/Timeline";
import { Inspector } from "./editor/Inspector";
import { ToolPanel } from "./editor/ToolPanel";
import { ExportModal } from "./editor/ExportModal";

const IconBtn = ({ onClick, title, children, active, testid }) => (
  <button onClick={onClick} title={title} data-testid={testid} className={`h-8 w-8 grid place-items-center rounded-lg transition-colors ${active ? "text-black" : "text-zinc-300 hover:text-white hover:bg-white/10"}`} style={active ? { background: Y } : {}}>{children}</button>
);
const KEYS = [["Espacio", "Play / Pausa"], ["S", "Dividir en el cabezal"], ["Ctrl + D", "Duplicar"], ["Supr", "Eliminar selección"], ["Ctrl + Z / Y", "Deshacer / Rehacer"], ["← →", "Fotograma a fotograma"]];

export default function VideoEditor({ onBack, initialClip, aiClips = [], storyboard = null }) {
  const { user, login } = useAuth();
  const [tool, setTool] = useState("media");
  const [project, setProject] = useState(initialClip?.title || "Proyecto sin título");
  const [media, setMedia] = useState([]);
  const [clips, setClips] = useState([]);
  const [overlays, setOverlays] = useState([]);
  const [audios, setAudios] = useState([]);
  const [sel, setSel] = useState(null); // {kind, id, tab?}
  const [playing, setPlaying] = useState(false);
  const [gt, setGt] = useState(0);
  const [aspect, setAspect] = useState(ASPECTS[0]);
  const [filter, setFilter] = useState("none");
  const [adj, setAdj] = useState({ brightness: 100, contrast: 100, saturate: 100 });
  const [mainVolume, setMainVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [snap, setSnap] = useState(true);
  const [trDur, setTrDur] = useState(0.5);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showKeys, setShowKeys] = useState(false);
  const [limits, setLimits] = useState(null);
  const [exp, setExp] = useState({ resolution: 720, fps: 30, fmt: "mp4" });
  const [exporting, setExporting] = useState(false);
  const [expPct, setExpPct] = useState(0);
  const [size, setSize] = useState({ w: 260, h: 460 });
  const [aiBusy, setAiBusy] = useState({});
  const [panelOpen, setPanelOpen] = useState(true);
  const [inspOpen, setInspOpen] = useState(true);
  const [sub, setSubState] = useState({ emoji: true, style: "classic", cs: null, hl: "#FFD60A", font: "default", color: "#FFFFFF", size: 44, stroke: true, y: 0.78 });
  const [textFont, setTextFont] = useState("default");
  const [usage, setUsage] = useState(null);

  const videoRef = useRef(null);
  const stageRef = useRef(null);
  const fsRef = useRef(null);
  const [fs, setFs] = useState(false);
  const [subLang, setSubLang] = useState(initialClip?.subLang || "original");
  const [translating, setTranslating] = useState(false);
  const fileInput = useRef(null);
  const audioEls = useRef({});
  const gtRef = useRef(0);
  const playRef = useRef(false);
  const st = useRef({});
  const hist = useRef({ list: [], idx: -1 });
  const didInit = useRef(false);

  const lay = useMemo(() => layout(clips), [clips]);
  const total = totalOf(lay);
  const assets = useMemo(() => Object.fromEntries(media.map((m) => [m.id, m])), [media]);
  st.current = { clips, lay, total, audios, muted, mainVolume };
  const selItem = sel && (sel.kind === "clip" ? clips.find((c) => c.id === sel.id) : sel.kind === "overlay" ? overlays.find((o) => o.id === sel.id) : audios.find((a) => a.id === sel.id));
  const selK = sel?.kind === "clip" ? clips.findIndex((c) => c.id === sel.id) : -1;

  // ---- history (debounced snapshots) ----
  const snapStr = () => JSON.stringify({ clips, overlays, audios, aspect: aspect[0], filter, adj, mainVolume, muted });
  useEffect(() => {
    const t = setTimeout(() => { const h = hist.current, s = snapStr(); if (h.list[h.idx] === s) return; h.list = h.list.slice(0, h.idx + 1); h.list.push(s); h.idx = h.list.length - 1; }, 250);
    return () => clearTimeout(t); // eslint-disable-next-line
  }, [clips, overlays, audios, aspect, filter, adj, mainVolume, muted]);
  const applySnap = (s) => { const d = JSON.parse(s); setClips(d.clips); setOverlays(d.overlays); setAudios(d.audios); setFilter(d.filter); setAdj(d.adj); setMainVolume(d.mainVolume); setMuted(d.muted); setAspect(ASPECTS.find((a) => a[0] === d.aspect) || ASPECTS[0]); };
  const undo = useCallback(() => { const h = hist.current; if (h.idx > 0) { h.idx--; applySnap(h.list[h.idx]); } }, []);
  const redo = useCallback(() => { const h = hist.current; if (h.idx < h.list.length - 1) { h.idx++; applySnap(h.list[h.idx]); } }, []);

  // ---- playback engine: video element is the clock while a clip plays ----
  const seek = useCallback((t) => { const v = clamp(t, 0, Math.max(0, st.current.total)); gtRef.current = v; setGt(v); }, []);
  const togglePlay = useCallback(() => {
    if (!st.current.clips.length) return;
    if (!playRef.current && gtRef.current >= st.current.total - 0.05) seek(0);
    playRef.current = !playRef.current; setPlaying(playRef.current);
  }, [seek]);

  useEffect(() => {
    let raf, last = performance.now();
    const loop = (now) => {
      const dt = Math.min(0.1, (now - last) / 1000); last = now;
      const { clips: cs, lay: ly, total: T, audios: au, muted: mu, mainVolume: mv } = st.current;
      const v = videoRef.current;
      let t = gtRef.current;
      if (playRef.current && cs.length) {
        const k = activeIdx(ly, t), c = cs[k];
        if (v && c && v.dataset.cid === c.id && !v.paused && v.readyState >= 2) {
          t = ly[k].start + (v.currentTime - c.in) / c.speed;
          if (v.currentTime >= c.out - 0.03) t = ly[k].start + ly[k].len + 0.001;
        } else t += dt;
        if (t >= T) { t = T; playRef.current = false; setPlaying(false); }
        if (Math.abs(t - gtRef.current) > 0.0005) { gtRef.current = t; setGt(t); }
      }
      if (v && cs.length) {
        const k = activeIdx(ly, t), c = cs[k];
        const local = c.in + (t - ly[k].start) * c.speed;
        if (v.dataset.cid !== c.id) { v.dataset.cid = c.id; v.src = c.url; v.addEventListener("loadedmetadata", () => { v.currentTime = local; }, { once: true }); }
        else if (v.readyState >= 1 && Math.abs(v.currentTime - local) > (playRef.current ? 0.35 : 0.04)) v.currentTime = local;
        v.playbackRate = c.speed; v.muted = mu; v.volume = clamp(mv, 0, 1);
        if (playRef.current && v.paused && v.readyState >= 2) v.play().catch(() => {});
        if (!playRef.current && !v.paused) v.pause();
      } else if (v && !cs.length && v.dataset.cid) { v.removeAttribute("src"); delete v.dataset.cid; v.load(); }
      au.forEach((a) => {
        const el = audioEls.current[a.id]; if (!el) return;
        const local = a.in + (t - a.start), on = playRef.current && t >= a.start && local < a.out;
        if (on) {
          const fi = a.fadeIn > 0 ? clamp((local - a.in) / a.fadeIn, 0, 1) : 1, fo = a.fadeOut > 0 ? clamp((a.out - local) / a.fadeOut, 0, 1) : 1;
          el.volume = clamp(a.volume * fi * fo, 0, 1);
          if (Math.abs(el.currentTime - local) > 0.3) el.currentTime = local;
          if (el.paused) el.play().catch(() => {});
        } else if (!el.paused) el.pause();
      });
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const els = audioEls.current;
    audios.forEach((a) => { if (!els[a.id]) els[a.id] = new Audio(a.url); });
    Object.keys(els).forEach((id) => { if (!audios.find((a) => a.id === id)) { els[id].pause(); delete els[id]; } });
  }, [audios]);
  useEffect(() => () => Object.values(audioEls.current).forEach((el) => el.pause()), []);

  // stage size for proportional text/sticker sizing
  useEffect(() => {
    const el = stageRef.current; if (!el) return;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el); return () => ro.disconnect();
  }, []);

  useEffect(() => { if (user) api.get("/editor/limits").then(({ data }) => setLimits(data)).catch(() => {}); }, [user]);
  useEffect(() => { if (user && showExport) api.get("/me/usage").then(({ data }) => setUsage(data)).catch(() => {}); }, [user, showExport]);

  // ---- builders ----
  const mkClip = (a) => ({ id: uid("c"), mediaId: a.id, name: a.name, preview: !!a.preview, url: a.localUrl, srcDur: a.duration || 10, in: Math.min(a.lead || 0, Math.max(0, (a.duration || 10) - 1)), out: a.duration || 10, speed: 1, transition: { type: "none", dur: 0.5 } });
  const ovSpan = () => { const s = total > 0 ? Math.min(gtRef.current, Math.max(0, total - 0.5)) : 0; return { start: s, end: total > 0 ? Math.min(total, s + 3) : s + 3 }; };
  // free time slot for a new subtitle: starts at the playhead (or right after a subtitle under it) and stops before the next one
  const subSlot = () => {
    const subs = overlays.filter((o) => o.track === "sub").sort((a, b) => a.start - b.start);
    let { start } = ovSpan();
    const under = subs.find((o) => o.start <= start && start < o.end);
    if (under) start = under.end;
    const next = subs.find((o) => o.start >= start);
    const end = Math.min(start + 3, next ? next.start : Infinity, total > 0 ? total : Infinity);
    return end - start > 0.3 ? { start, end } : { start, end: start + 3 };
  };

  const addOverlay = (o) => { const id = uid("o"); setOverlays((p) => [...p, { id, x: 0.5, y: 0.5, rot: 0, animIn: "none", animOut: "none", ...ovSpan(), ...o }]); setSel({ kind: "overlay", id }); };
  const addText = (style = { color: "#FFFFFF", stroke: true }) => addOverlay({ type: "text", text: "Tu texto", size: 64, font: textFont, ...style });
  const setSub = (patch) => {
    setSubState((s) => ({ ...s, ...patch }));
    const { style, emoji, ...ov } = patch;
    setOverlays((p) => p.map((o) => {
      if (o.track !== "sub") return o;
      const raw = o.raw ?? o.text.replace(/\n/g, " ").replace(/\s?\p{Extended_Pictographic}\uFE0F?/gu, "");
      return { ...o, ...ov, ...(emoji === undefined ? {} : { raw, text: wrapText(emoji ? (o.emo || emojify(raw)) : raw) }) };
    }));
  };
  // AI emojis for the current subtitle lines (by meaning); only applied while emojis are on
  const loadEmojis = (subs, context) => api.post("/clips/emojis", { lines: subs.map((o) => o.raw), context }).then(({ data }) => {
    const m = new Map(subs.map((o, i) => [o.id, data.lines[i]]));
    setOverlays((p) => p.map((o) => {
      const emo = m.get(o.id);
      if (!emo || o.raw === undefined) return o;
      return { ...o, emo, ...(o.text === wrapText(emojify(o.raw)) ? { text: wrapText(emo) } : {}) };
    }));
  }).catch(() => {});
  const pickSubLang = async (code) => {
    const subs = overlays.filter((o) => o.track === "sub");
    setSubLang(code);
    if (!subs.length) return;
    const orig = subs.map((o) => o.orig ?? o.raw ?? o.text);
    setTranslating(true);
    try {
      const lines = code === "original" ? orig : (await api.post("/clips/translate", { lines: orig, target: code })).data.lines;
      const m = new Map(subs.map((o, i) => [o.id, lines[i] || orig[i]]));
      const next = overlays.map((o) => (m.has(o.id) ? { ...o, orig: o.orig ?? o.raw, raw: m.get(o.id), emo: undefined, text: wrapText(sub.emoji ? emojify(m.get(o.id)) : m.get(o.id)) } : o));
      setOverlays(next);
      if (sub.emoji) loadEmojis(next.filter((o) => o.track === "sub"), initialClip?.title || "");
      toast.success("Subtítulos actualizados");
    } catch (e) { toast.error("No se pudieron traducir los subtítulos"); setSubLang(subLang); }
    setTranslating(false);
  };
  const titleOv = overlays.find((o) => o.track === "title");
  const setTitleSize = (v) => titleOv && updOv(titleOv.id, { size: v });
  const toggleFs = () => {
    if (fs) { if (document.fullscreenElement) document.exitFullscreen().catch(() => {}); setFs(false); return; }
    setFs(true);
    // real fullscreen where supported; otherwise the fixed overlay acts as fullscreen (iPhone)
    setTimeout(() => fsRef.current?.requestFullscreen?.().catch(() => {}), 0);
  };
  useEffect(() => {
    const h = () => { if (!document.fullscreenElement) setFs(false); };
    document.addEventListener("fullscreenchange", h); return () => document.removeEventListener("fullscreenchange", h);
  }, []);
  const addSub = () => addOverlay({ ...subSlot(), track: "sub", type: "text", text: "Subtítulo", cs: sub.cs, hl: sub.hl, font: sub.font, color: sub.color, size: sub.size, stroke: sub.stroke, x: 0.5, y: sub.y });
  const pickTextFont = (k) => { setTextFont(k); if (sel?.kind === "overlay" && selItem?.type === "text") updOv(sel.id, { font: k }); };
  const addEmoji = (em) => addOverlay({ type: "text", text: em, size: 110, color: "#FFFFFF", stroke: false });
  const addSticker = (url, mediaId) => addOverlay({ type: "image", url, mediaId, w: 0.3 });
  const addAsset = (m) => {
    if (m.kind === "video") setClips((p) => [...p, mkClip(m)]);
    else if (m.kind === "image") addSticker(m.localUrl, m.id);
    else { const id = uid("a"); setAudios((p) => [...p, { id, mediaId: m.id, name: m.name, url: m.localUrl, srcDur: m.duration || 10, start: gtRef.current, in: 0, out: Math.min(m.duration || 10, Math.max(1, (total || 30) - gtRef.current)), volume: 1, fadeIn: 0, fadeOut: 0 }]); setSel({ kind: "audio", id }); }
  };

  const uploadFiles = async (files) => {
    if (!user) { toast.info("Inicia sesión para importar"); login(); return; }
    setUploading(true);
    for (const file of files) {
      try {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const localUrl = URL.createObjectURL(file);
        const duration = data.kind === "image" ? 0 : await probeDur(localUrl, data.kind);
        const asset = { ...data, localUrl, duration, peaks: data.kind === "audio" ? await peaksOf(localUrl) : null };
        setMedia((p) => [...p, asset]);
        if (data.kind === "video") setClips((p) => (p.length ? p : [mkClip(asset)]));
        toast.success(`${data.name} importado`);
      } catch (e) { toast.error("Error al importar " + file.name); }
    }
    setUploading(false);
  };

  // ---- open the AI-trimmed clip from the Studio ----
  useEffect(() => {
    if (!initialClip || didInit.current) return;
    if (!user) { toast.info("Inicia sesión para editar este clip"); return; }
    didInit.current = true;
    const c = initialClip, len = Math.max(1, (c.end || 0) - (c.start || 0));
    const ovs = [];
    if (c.hook) ovs.push({ id: uid("o"), track: "title", type: "text", text: wrapText(c.hook.toUpperCase(), 18), size: 50, color: "#FFFFFF", stroke: true, x: 0.5, y: 0.14, rot: 0, start: 0, end: len, animIn: "zoom", animOut: "none" });
    const caps = (c.captions || []).slice().sort((a, b) => a.start - b.start);
    caps.forEach((cap, i) => {
      // each subtitle ends when the next one starts, so they never stack on top of each other
      const next = caps[i + 1] ? caps[i + 1].start - c.start - 0.3 : Infinity;
      const s = Math.max(0, cap.start - c.start - 0.3), e = Math.min(len, cap.end - c.start - 0.3, next);
      const emo = (c.emoMap || c.asset?.emoMap)?.[cap.text];
      if (e - s > 0.15) ovs.push({ id: uid("o"), track: "sub", type: "text", raw: cap.text, orig: cap.orig ?? cap.text, emo, text: wrapText(sub.emoji ? (emo || emojify(cap.text)) : cap.text), cs: sub.cs, hl: sub.hl, size: sub.size, color: sub.color, stroke: sub.stroke, font: sub.font, x: 0.5, y: sub.y, rot: 0, start: s, end: e, animIn: "none", animOut: "none" });
    });
    setOverlays(ovs);
    const subs = ovs.filter((o) => o.track === "sub");
    // emojis were usually prepared while the clip was loading (emoMap); only ask the AI for what's missing
    if (subs.some((o) => !o.emo)) loadEmojis(subs, c.title);
    if (c.asset !== undefined) {
      if (c.asset) { const a = { ...c.asset, duration: c.asset.duration || len }; setMedia([a]); setClips([mkClip(a)]); setAiBusy({ [c.id]: "done" }); }
      return;
    }
    setImporting(true);
    (async () => {
      try {
        const { data: job } = await api.post("/editor/import-clip", { video_id: c.videoId, start: c.start, end: c.end, title: c.title });
        let data = null;
        for (let i = 0; i < 150 && !data; i++) {
          await new Promise((r) => setTimeout(r, 1200));
          const { data: st } = await api.get(`/editor/import-clip/${job.job_id}`);
          if (st.status === "done") data = st.asset;
          else if (st.status === "error") throw new Error(st.error);
        }
        if (!data) throw new Error("timeout");
        const res = await api.get(`/media/${data.id}`, { responseType: "blob" });
        const asset = { ...data, localUrl: URL.createObjectURL(res.data), duration: data.duration || len };
        setMedia((p) => [...p, asset]);
        setClips([mkClip(asset)]);
        toast.success("Clip de la IA listo para editar");
      } catch (e) {
        toast.error("YouTube bloqueó la descarga del clip. Importa el vídeo manualmente; los textos y subtítulos ya están listos.", { duration: 8000 });
      } finally { setImporting(false); }
    })(); // eslint-disable-next-line
  }, [initialClip, user]);

  // ---- add any other AI-trimmed clip from the analysis to the timeline ----
  const importAi = async (c) => {
    if (!user) { login(); return; }
    if (aiBusy[c.id]) return;
    setAiBusy((b) => ({ ...b, [c.id]: "loading" }));
    try {
      const { data: job } = await api.post("/editor/import-clip", { video_id: c.videoId, start: c.start, end: c.end, title: c.title });
      let data = null;
      for (let i = 0; i < 150 && !data; i++) {
        await new Promise((r) => setTimeout(r, 1200));
        const { data: st } = await api.get(`/editor/import-clip/${job.job_id}`);
        if (st.status === "done") data = st.asset; else if (st.status === "error") throw new Error(st.error);
      }
      if (!data) throw new Error("timeout");
      const res = await api.get(`/media/${data.id}`, { responseType: "blob" });
      const asset = { ...data, localUrl: URL.createObjectURL(res.data), duration: data.duration || c.end - c.start };
      setMedia((p) => [...p, asset]);
      setClips((p) => [...p, mkClip(asset)]);
      setAiBusy((b) => ({ ...b, [c.id]: "done" }));
      setProject((p) => (p === "Proyecto sin título" ? c.title : p));
      toast.success(`«${c.title}» añadido a la línea de tiempo`);
    } catch (e) {
      setAiBusy((b) => ({ ...b, [c.id]: undefined }));
      toast.error("No se pudo descargar ese clip. Reinténtalo.");
    }
  };

  // ---- mutations ----
  const updClip = (id, patch) => setClips((p) => p.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const updOv = (id, patch) => setOverlays((p) => p.map((o) => (o.id === id ? { ...o, ...patch, ...("text" in patch ? { raw: undefined, emo: undefined } : {}) } : o)));
  const updAu = (id, patch) => setAudios((p) => p.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  const reorder = (from, to) => setClips((p) => { const a = [...p]; const [x] = a.splice(from, 1); a.splice(to, 0, x); return a; });
  const delSel = useCallback(() => {
    if (!sel) return;
    if (sel.kind === "clip") setClips((p) => p.filter((c) => c.id !== sel.id));
    if (sel.kind === "overlay") setOverlays((p) => p.filter((o) => o.id !== sel.id));
    if (sel.kind === "audio") setAudios((p) => p.filter((a) => a.id !== sel.id));
    setSel(null);
  }, [sel]);
  const dupSel = useCallback(() => {
    if (!sel) return;
    if (sel.kind === "overlay") { const o = overlays.find((x) => x.id === sel.id); if (!o) return; const id = uid("o"); const L = o.end - o.start; setOverlays((p) => [...p, { ...o, id, track: undefined, start: o.end, end: o.end + L }]); setSel({ kind: "overlay", id }); }
    if (sel.kind === "clip") { const k = clips.findIndex((c) => c.id === sel.id); if (k < 0) return; const id = uid("c"); setClips((p) => { const a = [...p]; a.splice(k + 1, 0, { ...p[k], id }); return a; }); setSel({ kind: "clip", id }); }
    if (sel.kind === "audio") { const a0 = audios.find((x) => x.id === sel.id); if (!a0) return; const id = uid("a"); setAudios((p) => [...p, { ...a0, id, start: a0.start + (a0.out - a0.in) }]); setSel({ kind: "audio", id }); }
  }, [sel, overlays, clips, audios]);
  const split = useCallback(() => {
    const t = gtRef.current;
    if (sel?.kind === "overlay") {
      const o = overlays.find((x) => x.id === sel.id); if (!o || t <= o.start + 0.1 || t >= o.end - 0.1) { toast.info("Coloca el cabezal dentro del elemento"); return; }
      setOverlays((p) => [...p.map((x) => (x.id === o.id ? { ...x, end: t } : x)), { ...o, id: uid("o"), start: t }]); return;
    }
    if (sel?.kind === "audio") {
      const a = audios.find((x) => x.id === sel.id); const at = a && a.in + (t - a.start);
      if (!a || at <= a.in + 0.1 || at >= a.out - 0.1) { toast.info("Coloca el cabezal dentro del audio"); return; }
      setAudios((p) => [...p.map((x) => (x.id === a.id ? { ...x, out: at } : x)), { ...a, id: uid("a"), in: at, start: t }]); return;
    }
    if (!clips.length) return;
    const k = activeIdx(lay, t), c = clips[k], local = c.in + (t - lay[k].start) * c.speed;
    if (local <= c.in + 0.15 || local >= c.out - 0.15) { toast.info("Coloca el cabezal dentro del clip"); return; }
    setClips((p) => { const a = [...p]; a.splice(k, 1, { ...c, out: local }, { ...c, id: uid("c"), in: local, transition: { type: "none", dur: 0.5 } }); return a; });
  }, [sel, overlays, audios, clips, lay]);

  const setTransition = (type) => {
    const k = selK > 0 ? selK : -1;
    if (k < 0) { toast.info(clips.length > 1 ? "Selecciona un clip (no el primero) o usa «Aplicar a todos»" : "Necesitas al menos 2 clips para usar transiciones"); return; }
    updClip(clips[k].id, { transition: { type, dur: trDur } });
  };
  const applyAllTransitions = () => {
    if (clips.length < 2) { toast.info("Necesitas al menos 2 clips"); return; }
    const type = (selK > 0 && clips[selK].transition?.type !== "none" && clips[selK].transition.type) || "fade";
    setClips((p) => p.map((c, i) => (i ? { ...c, transition: { type, dur: trDur } } : c)));
    toast.success("Transición aplicada a todos los cortes");
  };

  // ---- keyboard ----
  useEffect(() => {
    const h = (e) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) return;
      const k = e.key.toLowerCase();
      if (e.key === "Escape" && fsRef.current?.dataset.fs === "1") { setFs(false); return; }
      if (e.code === "Space") { e.preventDefault(); togglePlay(); }
      else if (e.key === "Delete" || e.key === "Backspace") delSel();
      else if ((e.ctrlKey || e.metaKey) && k === "z") { e.preventDefault(); undo(); }
      else if ((e.ctrlKey || e.metaKey) && k === "y") { e.preventDefault(); redo(); }
      else if ((e.ctrlKey || e.metaKey) && k === "d") { e.preventDefault(); dupSel(); }
      else if (k === "s" && !e.ctrlKey && !e.metaKey) split();
      else if (e.key === "ArrowRight") seek(gtRef.current + 1 / 30);
      else if (e.key === "ArrowLeft") seek(gtRef.current - 1 / 30);
    };
    window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h);
  }, [togglePlay, delSel, undo, redo, dupSel, split, seek]);

  // ---- export ----
  const doExport = async () => {
    if (!user) { toast.info("Inicia sesión para exportar"); login(); return; }
    if (!clips.length) { toast.error("Añade al menos un clip de vídeo"); return; }
    if (usage && usage.exports_remaining <= 0) { toast.error("No te quedan exportaciones. Mejora tu plan para seguir exportando."); return; }
    setExporting(true); setExpPct(0);
    const tid = toast.loading("Renderizando vídeo…");
    try {
      const payload = {
        title: project, aspect: aspect[0], resolution: exp.resolution, fps: exp.fps, fmt: exp.fmt,
        main_volume: muted ? 0 : mainVolume, color: { filter, ...adj },
        clips: clips.map((c) => ({ media_id: c.mediaId, in: c.in, out: c.out, speed: c.speed, transition: c.transition })),
        overlays: overlays.map((o) => ({ type: o.type, text: o.text, font: o.font, cs: o.cs || undefined, hl: o.hl, color: o.color, size: o.size, stroke: !!o.stroke, x: o.x, y: o.y, w: o.w, rot: o.rot, start: o.start, end: o.end, anim_in: o.animIn, anim_out: o.animOut, media_id: o.mediaId, url: o.mediaId ? undefined : o.url })),
        audios: audios.map((a) => ({ media_id: a.mediaId, start: a.start, in: a.in, out: a.out, volume: a.volume, fade_in: a.fadeIn, fade_out: a.fadeOut })),
      };
      const { data: job } = await api.post("/editor/export-job", payload);
      let data = null;
      for (let i = 0; i < 1200 && !data; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        const { data: st } = await api.get(`/editor/export-job/${job.job_id}`);
        if (st.status === "running") { setExpPct(st.progress || 0); toast.loading(`Renderizando vídeo… ${st.progress || 0}%`, { id: tid }); }
        if (st.status === "done") data = st.result;
        else if (st.status === "error") throw Object.assign(new Error(st.error), { response: { data: { detail: st.error } } });
      }
      if (!data) throw new Error("timeout");
      const res = await api.get(`/clips/download/${data.export_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "video/mp4" }));
      const a = document.createElement("a"); a.href = url; a.download = `${project.replace(/[\\/:*?"<>|\n]+/g, "").trim().slice(0, 80) || "Vitled"}.${exp.fmt}`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success(`¡«${project}» exportado!`); setShowExport(false); refreshUsage();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d === "plan_quality_locked" ? "Esa calidad requiere un plan de pago." : d === "export_limit_reached" || e?.response?.status === 402 ? "No te quedan exportaciones. Mejora tu plan." : "No se pudo exportar.");
      if (d === "export_limit_reached" || e?.response?.status === 402) api.get("/me/usage").then(({ data }) => setUsage(data)).catch(() => {});
    } finally { toast.dismiss(tid); setExporting(false); }
  };

  // transition preview: brief opacity dip around each cut
  let dip = 1;
  lay.forEach((l, k) => { if (k > 0 && l.td > 0 && gt >= l.start && gt <= l.start + l.td) { const p = (gt - l.start) / l.td; dip = 0.25 + 0.75 * Math.abs(1 - 2 * p); } });
  const stageStyle = { aspectRatio: String(aspect[1]), ...(fs ? { width: `min(100vw, ${aspect[1] * 100}vh)`, borderRadius: 0, border: 0 } : aspect[1] > 1 ? { width: "min(100%, 760px)" } : { height: "min(50vh, 460px)", maxWidth: "100%" }), filter: `${FILTERS[filter]} brightness(${adj.brightness}%) contrast(${adj.contrast}%) saturate(${adj.saturate}%)` };
  const pps = 40 * zoom;

  return (
    <div dir="ltr" className="rounded-xl overflow-hidden border border-white/10 bg-[#0D0D0D] text-white select-none" data-testid="video-editor">
      {/* 1. TOP BAR */}
      <div className="relative flex items-center justify-between gap-3 h-14 px-3 bg-gradient-to-b from-[#1F1F1F] to-[#171717] border-b border-white/10">
        <span className="absolute inset-x-0 -bottom-px h-px bg-gradient-to-r from-transparent via-yellow-400/60 to-transparent pointer-events-none" />
        <div className="flex items-center gap-3">
          <img src={logo} alt="Vitled" className="h-7 w-7" />
          <span className="font-black text-sm hidden md:block">Vit<span style={{ color: Y }}>led</span></span>
          <div className="flex gap-1 sm:ml-2">
            <IconBtn onClick={() => setPanelOpen((o) => !o)} title={panelOpen ? "Ocultar panel" : "Mostrar panel"} testid="toggle-tool-panel">{panelOpen ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeftOpen className="h-4 w-4" />}</IconBtn>
            <button onClick={onBack} data-testid="editor-back" className="text-xs text-zinc-400 hover:text-white px-2 py-1 rounded">Volver</button>
            <button onClick={() => fileInput.current?.click()} data-testid="editor-import-top" className="hidden sm:block text-xs text-zinc-400 hover:text-white px-2 py-1 rounded">Importar</button>
          </div>
        </div>
        <div className="flex items-center gap-2 min-w-0">
          {initialClip && <span className="hidden md:inline-flex items-center gap-1 rounded-full bg-yellow-400 px-2.5 py-0.5 text-[10px] font-black" data-testid="ai-clip-badge"><Sparkles className="h-3 w-3" />CLIP IA · {initialClip.score}%</span>}
          <input value={project} onChange={(e) => setProject(e.target.value)} className="bg-white/[0.03] border border-white/10 hover:border-white/20 text-center text-sm font-semibold focus:outline-none focus:border-yellow-400/60 rounded-lg px-3 py-1.5 w-28 sm:w-64 truncate transition-colors" data-testid="project-name" />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="hidden sm:contents"><IconBtn onClick={() => setShowKeys(true)} title="Atajos" testid="shortcuts-btn"><Keyboard className="h-4 w-4" /></IconBtn>
          <IconBtn onClick={undo} title="Deshacer" testid="undo-top"><Undo2 className="h-4 w-4" /></IconBtn>
          <IconBtn onClick={redo} title="Rehacer" testid="redo-top"><Redo2 className="h-4 w-4" /></IconBtn></span>
          <Button onClick={() => setShowExport(true)} data-testid="export-open" className="font-extrabold rounded-lg px-3 sm:px-5 ml-1 text-black hover:opacity-90" style={{ background: Y }}><Download className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Exportar</span></Button>
        </div>
      </div>
      <input ref={fileInput} type="file" multiple accept="video/*,image/*,audio/*" className="hidden" data-testid="file-input" onChange={(e) => { uploadFiles(Array.from(e.target.files)); e.target.value = ""; }} />

      <div className="flex flex-col lg:flex-row lg:h-[min(62vh,600px)]">
        {panelOpen ? <ToolPanel tool={tool} setTool={setTool} media={media} aiClips={aiClips} aiBusy={aiBusy} importAi={importAi} storyboard={storyboard} uploading={uploading} pickFile={() => fileInput.current?.click()} addAsset={addAsset}
          subLang={subLang} pickSubLang={pickSubLang} translating={translating} titleSize={titleOv ? titleOv.size : null} setTitleSize={setTitleSize}
          addText={addText} addEmoji={addEmoji} addSticker={(u) => addSticker(u)} selClip={selK >= 0 ? clips[selK] : null}
          selOverlay={sel?.kind === "overlay" ? selItem : null} setTransition={setTransition} applyAllTransitions={applyAllTransitions}
          trDur={trDur} setTrDur={setTrDur} setAnim={(patch) => sel && updOv(sel.id, patch)} filter={filter} setFilter={setFilter} adj={adj} setAdj={setAdj}
          sub={sub} setSub={setSub} addSub={addSub} subCount={overlays.filter((o) => o.track === "sub").length} textFont={textFont} pickTextFont={pickTextFont} />
          : <button onClick={() => setPanelOpen(true)} data-testid="show-tool-panel" title="Mostrar panel" className="order-2 lg:order-none hidden lg:flex flex-col items-center gap-2 w-10 shrink-0 border-r border-white/10 bg-[#1A1A1A] pt-4 text-zinc-400 hover:text-white hover:bg-[#202020] transition-colors"><PanelLeftOpen className="h-4 w-4" /><span className="text-[10px] font-semibold [writing-mode:vertical-rl] rotate-180 tracking-wider">HERRAMIENTAS</span></button>}

        {/* 3. PREVIEW */}
        <div className="relative order-1 lg:order-none flex-1 min-w-0 flex flex-col items-center justify-center gap-3 p-3 sm:p-4 bg-[#0B0B0B] overflow-hidden">
          <div className="absolute inset-0 pointer-events-none opacity-[0.35]" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,.07) 1px, transparent 1px)", backgroundSize: "18px 18px" }} />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-80 w-80 rounded-full bg-yellow-400/[0.07] blur-3xl pointer-events-none" />
          <span className="absolute top-3 left-3 rounded-md border border-white/10 bg-black/60 px-2 py-1 text-[10px] font-mono text-zinc-400" data-testid="canvas-format">{aspect[0]} · {fmtMs(total)}</span>
          {clips.some((c) => c.preview) && (
            <div className="absolute top-3 right-3 z-20 max-w-[300px] rounded-xl border border-amber-400/40 bg-amber-500/15 backdrop-blur p-3 text-xs text-amber-100" data-testid="preview-clip-banner">
              <b className="block text-amber-300">Vista previa sin audio</b>
              YouTube bloqueó la descarga de este vídeo, así que el clip está hecho con fotogramas. Sube el vídeo original para exportar en calidad real.
              <button onClick={() => fileInput.current?.click()} data-testid="preview-upload-original" className="mt-2 block rounded-lg bg-amber-400 px-3 py-1.5 font-bold text-black hover:bg-amber-300">Subir vídeo original</button>
            </div>
          )}
          <div ref={fsRef} data-fs={fs ? "1" : "0"} data-testid="fullscreen-wrap" className={fs ? "fixed inset-0 z-[95] bg-black flex items-center justify-center" : "contents"}>
            <Stage stageRef={stageRef} videoRef={videoRef} size={size} style={stageStyle} overlays={overlays} gt={gt} dip={dip} importing={importing}
              selId={fs ? null : sel?.kind === "overlay" ? sel.id : null} onSelect={(id) => !fs && setSel(id ? { kind: "overlay", id } : null)} onUpdate={updOv}
              onDelete={(id) => { setOverlays((p) => p.filter((o) => o.id !== id)); setSel(null); }} hasClips={clips.length > 0} />
            {fs && (
              <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-3 rounded-full bg-black/70 backdrop-blur border border-white/15 px-3 py-2" data-testid="fs-controls">
                <button onClick={togglePlay} data-testid="fs-play" className="h-10 w-10 rounded-full grid place-items-center text-black" style={{ background: Y }}>{playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5 fill-black" />}</button>
                <span className="text-xs text-zinc-200 tabular-nums">{fmtMs(gt)} / {fmtMs(total)}</span>
                <button onClick={toggleFs} data-testid="fs-exit" title="Salir de pantalla completa" className="h-10 w-10 rounded-full grid place-items-center text-white hover:bg-white/15"><Minimize className="h-5 w-5" /></button>
              </div>
            )}
          </div>
          <div className="relative flex items-center gap-2 sm:gap-3 w-full max-w-[560px] rounded-2xl border border-white/10 bg-[#151515]/90 backdrop-blur px-3 py-2">
            <IconBtn onClick={() => seek(0)} title="Inicio" testid="seek-start"><SkipBack className="h-4 w-4" /></IconBtn>
            <button onClick={togglePlay} data-testid="play" className="h-9 w-9 rounded-full grid place-items-center text-black shrink-0 hover:scale-105 transition-transform" style={{ background: Y }}>{playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5 fill-black" />}</button>
            <button onClick={toggleFs} data-testid="fullscreen-btn" title="Pantalla completa" className="h-9 w-9 rounded-full grid place-items-center text-white bg-white/10 hover:bg-white/20 shrink-0 transition-colors"><Maximize className="h-4 w-4" /></button>
            <span className="text-xs text-zinc-400 tabular-nums" data-testid="time-display">{fmtMs(gt)} / {fmtMs(total)}</span>
            <div className="flex-1 h-1.5 bg-white/10 rounded-full cursor-pointer" data-testid="scrubber" onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); seek(((e.clientX - r.left) / r.width) * total); }}><div className="h-full rounded-full" style={{ width: `${total ? (gt / total) * 100 : 0}%`, background: Y }} /></div>
            <div className="flex shrink-0 rounded-lg bg-white p-0.5" data-testid="aspect-select">{ASPECTS.map((a) => (
              <button key={a[0]} onClick={() => setAspect(a)} data-testid={`aspect-${a[0].replace(":", "-")}`} className={`rounded-md px-1.5 sm:px-2 py-1 text-[11px] font-bold transition-colors ${aspect[0] === a[0] ? "bg-black text-white" : "text-black hover:bg-black/10"}`}>{a[0]}</button>
            ))}</div>
          </div>
        </div>

        {inspOpen ? <Inspector onHide={() => setInspOpen(false)} sel={sel} item={selItem} k={selK} count={clips.length} mainVolume={mainVolume} setMainVolume={setMainVolume}
          onUpd={(patch) => (sel.kind === "clip" ? updClip(sel.id, patch) : sel.kind === "overlay" ? updOv(sel.id, patch) : updAu(sel.id, patch))}
          onDel={delSel} onDup={dupSel} onMove={(d) => { reorder(selK, selK + d); }} />
          : <button onClick={() => setInspOpen(true)} data-testid="show-inspector" title="Mostrar propiedades" className="hidden lg:flex flex-col items-center gap-2 w-10 shrink-0 border-l border-white/10 bg-[#1A1A1A] pt-4 text-zinc-400 hover:text-white hover:bg-[#202020] transition-colors"><PanelRightOpen className="h-4 w-4" /><span className="text-[10px] font-semibold [writing-mode:vertical-rl] tracking-wider">PROPIEDADES</span></button>}
      </div>

      {/* 5. TIMELINE */}
      <div className="border-t border-white/10 bg-[#141414]">
        <div className="flex items-center gap-1 px-3 py-2 border-b border-white/10">
          <IconBtn onClick={split} title="Dividir (S)" testid="split-btn"><Scissors className="h-4 w-4" /></IconBtn>
          <IconBtn onClick={dupSel} title="Duplicar (Ctrl+D)" testid="duplicate-btn"><Copy className="h-4 w-4" /></IconBtn>
          <IconBtn onClick={delSel} title="Eliminar" testid="delete-btn"><Trash2 className="h-4 w-4" /></IconBtn>
          <IconBtn onClick={undo} title="Deshacer" testid="undo-btn"><Undo2 className="h-4 w-4" /></IconBtn>
          <IconBtn onClick={redo} title="Rehacer" testid="redo-btn"><Redo2 className="h-4 w-4" /></IconBtn>
          <span className="ml-3 text-[11px] text-zinc-500 hidden md:block">{clips.length} clip(s) · {overlays.length} capa(s) · {audios.length} audio(s)</span>
          <div className="ml-auto flex items-center gap-1">
            <IconBtn onClick={() => setZoom((z) => Math.max(0.25, z / 1.5))} title="Alejar" testid="zoom-out"><ZoomOut className="h-4 w-4" /></IconBtn>
            <IconBtn onClick={() => setZoom((z) => Math.min(6, z * 1.5))} title="Acercar" testid="zoom-in"><ZoomIn className="h-4 w-4" /></IconBtn>
            <IconBtn onClick={() => setSnap((s) => !s)} title="Imán" active={snap} testid="snap-btn"><Magnet className="h-4 w-4" /></IconBtn>
          </div>
        </div>
        <div className="px-1 py-2">
          <Timeline clips={clips} lay={lay} overlays={overlays} audios={audios} assets={assets} total={total} gt={gt} pps={pps} sel={sel} snap={snap}
            mainMuted={muted} onToggleMute={() => setMuted((m) => !m)} onSelect={setSel} onSeek={seek}
            onClip={updClip} onReorder={reorder} onOverlay={updOv} onAudio={updAu} />
        </div>
      </div>

      {showExport && <ExportModal exp={exp} setExp={setExp} limits={limits} usage={usage} exporting={exporting} progress={expPct} onExport={doExport} onClose={() => setShowExport(false)} total={total} />}
      {showKeys && (
        <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4" onClick={() => setShowKeys(false)}>
          <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-6 w-full max-w-sm text-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4"><p className="font-bold">Atajos de teclado</p><button onClick={() => setShowKeys(false)}><X className="h-5 w-5 text-zinc-400" /></button></div>
            {KEYS.map(([k, v]) => <div key={k} className="flex justify-between py-1.5 border-b border-white/5"><span className="text-zinc-400">{v}</span><kbd className="px-2 py-0.5 rounded bg-white/10 text-xs">{k}</kbd></div>)}
          </div>
        </div>
      )}
    </div>
  );
}
